/**
 * The origin/destination airport and port filters of the results: which sites
 * to offer, which quotes a pick keeps, and which extra lanes a pick needs. A
 * search starts on the nearest few sites; picking others here searches only
 * the lanes not searched yet.
 */
import type {
  LaneMode,
  LaneSearch,
  LaneSites,
  NormalizedQuote,
} from '../../../apis/lanePlanner';
import type { FilterGroup, FilterState } from './resultsView';
import type { PlaceValue } from './placeValue';

export type SiteEnd = 'origin' | 'destination';
export type SiteKind = 'airport' | 'port';

/** The boxes as they were when the search ran: where the candidate sites and their distances live. */
export interface SearchCandidates {
  origin: PlaceValue;
  destination: PlaceValue;
}

export interface SiteGroup extends FilterGroup {
  end: SiteEnd;
  kind: SiteKind;
  /** The sites the search started with: what the chip shows checked until it is changed. */
  defaults: string[];
}

const KIND_MODE: Record<SiteKind, LaneMode> = { airport: 'air', port: 'ocean' };
const KINDS: SiteKind[] = ['airport', 'port'];
const ENDS: SiteEnd[] = ['origin', 'destination'];
const END_LABEL: Record<SiteEnd, string> = {
  origin: 'Origin',
  destination: 'Destination',
};

export const siteKey = (end: SiteEnd, kind: SiteKind): string =>
  `${end}_${kind}`;

/** The lanes a search has covered in one mode, from its vendor rows and quotes. */
const routesOf = (search: LaneSearch, mode: LaneMode) => [
  ...search.vendors.filter((row) => row.mode === mode).map((r) => r.route),
  ...search.quotes.filter((quote) => quote.mode === mode).map((q) => q.route),
];

/** The airports or ports one end of the search already covers, from its lane rows and quotes. */
export const searchedSites = (
  search: LaneSearch,
  end: SiteEnd,
  kind: SiteKind
): string[] => {
  const codes = new Set<string>();
  for (const route of routesOf(search, KIND_MODE[kind])) {
    if (route?.[end]) codes.add(route[end]);
  }
  return [...codes];
};

const labelOf = (
  code: string,
  site?: PlaceValue['airports'][number]
): string => {
  if (!site) return code;
  const where = site.city || site.name;
  const distance =
    site.distance_km > 0 ? ` · ${Math.round(site.distance_km)} km` : '';
  return `${code}${where && where !== code ? ` · ${where}` : ''}${distance}`;
};

/** The site filters worth offering: a ticked mode and an end with more than one site to choose from. */
export const siteGroupsFor = (
  search: LaneSearch,
  candidates: SearchCandidates
): SiteGroup[] => {
  const groups: SiteGroup[] = [];
  for (const kind of KINDS) {
    if (!search.modes.includes(KIND_MODE[kind])) continue;
    for (const end of ENDS) {
      const near =
        candidates[end][kind === 'airport' ? 'airports' : 'ports'] ?? [];
      const known = new Set(near.map((site) => site.code));
      const options = [
        ...near.map((site) => ({
          value: site.code,
          label: labelOf(site.code, site),
        })),
        // a lane searched earlier that the boxes no longer list
        ...searchedSites(search, end, kind)
          .filter((code) => !known.has(code))
          .map((code) => ({ value: code, label: code })),
      ];
      if (options.length > 1) {
        const started = near.filter((site) => site.default).map((s) => s.code);
        groups.push({
          defaults: started.length ? started : searchedSites(search, end, kind),
          key: siteKey(end, kind),
          label: `${END_LABEL[end]} ${kind}`,
          options,
          end,
          kind,
        });
      }
    }
  }
  return groups;
};

/** Keeps quotes on the picked sites; other modes, and quotes without a route, are never hidden. */
export const applySiteFilters = (
  quotes: NormalizedQuote[],
  filters: FilterState
): NormalizedQuote[] => {
  const active = KINDS.flatMap((kind) =>
    ENDS.map((end) => ({
      kind,
      end,
      values: filters[siteKey(end, kind)] ?? [],
    }))
  ).filter((filter) => filter.values.length > 0);
  if (!active.length) return quotes;
  return quotes.filter((quote) =>
    active.every(
      ({ kind, end, values }) =>
        quote.mode !== KIND_MODE[kind] ||
        !quote.route ||
        values.includes(quote.route[end])
    )
  );
};

/** What the backend allows: new lanes in one add, and lanes in all, for one mode. */
export const MAX_NEW_LANES_PER_CALL = 9;
export const MAX_LANES_TOTAL = 25;

/** How many lanes an add would start, and how many the search would then hold (the busiest mode). */
export const laneBudget = (
  search: LaneSearch,
  lanes: { origin_sites: LaneSites; destination_sites: LaneSites }
): { newLanes: number; totalLanes: number } => {
  let newLanes = 0;
  let totalLanes = 0;
  for (const kind of KINDS) {
    const origins = lanes.origin_sites[kind];
    const destinations = lanes.destination_sites[kind];
    if (!origins || !destinations) continue;
    const searched = new Set(
      routesOf(search, KIND_MODE[kind]).flatMap((route) =>
        route && route.origin !== route.destination
          ? [`${route.origin}>${route.destination}`]
          : []
      )
    );
    const wanted = origins.flatMap((o) =>
      destinations.filter((d) => d !== o).map((d) => `${o}>${d}`)
    );
    const fresh = new Set(wanted.filter((lane) => !searched.has(lane)));
    newLanes = Math.max(newLanes, fresh.size);
    totalLanes = Math.max(totalLanes, searched.size + fresh.size);
  }
  return { newLanes, totalLanes };
};

/** The lanes to search for these picks: every picked site paired with the other end's picks (or its searched sites). Null when nothing new was picked. */
export const lanesToAdd = (
  search: LaneSearch,
  filters: FilterState
): { origin_sites: LaneSites; destination_sites: LaneSites } | null => {
  const origin_sites: LaneSites = {};
  const destination_sites: LaneSites = {};
  for (const kind of KINDS) {
    if (!search.modes.includes(KIND_MODE[kind])) continue;
    const picked = {
      origin: filters[siteKey('origin', kind)] ?? [],
      destination: filters[siteKey('destination', kind)] ?? [],
    };
    const searched = {
      origin: searchedSites(search, 'origin', kind),
      destination: searchedSites(search, 'destination', kind),
    };
    const isNew = ENDS.some((end) =>
      picked[end].some((code) => !searched[end].includes(code))
    );
    if (!isNew) continue;
    origin_sites[kind] = picked.origin.length ? picked.origin : searched.origin;
    destination_sites[kind] = picked.destination.length
      ? picked.destination
      : searched.destination;
  }
  if (!Object.keys(origin_sites).length) return null;
  const lanes = { origin_sites, destination_sites };
  // picks that only repeat the site at the other end (LAX to LAX) add nothing
  return laneBudget(search, lanes).newLanes > 0 ? lanes : null;
};
